import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const TeamForm = ({ team, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: team?.name || '',
    country: team?.country || '',
    logo: team?.logo || '',
    teamType: team?.teamType || 'country',
    leagueName: team?.leagueName || '',
    shortName: team?.shortName || ''
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const validate = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Team name is required';
    }

    if (formData?.teamType === 'country' && !formData?.country?.trim()) {
      newErrors.country = 'Country is required for country teams';
    }

    if (formData?.teamType === 'league' && !formData?.leagueName?.trim()) {
      newErrors.leagueName = 'League name is required for league teams';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validate()) return;

    setSubmitting(true);
    try {
      const submitData = {
        name: formData?.name?.trim(),
        country: formData?.teamType === 'country' ? formData?.country?.trim() : (formData?.country?.trim() || 'Multi-National'),
        logo: formData?.logo?.trim() || 'https://img.rocket.new/generatedImages/rocket_gen_img_16b616325-1766600649637.png',
        logoAlt: `${formData?.name?.trim()} team logo`,
        teamType: formData?.teamType,
        leagueName: formData?.teamType === 'league' ? formData?.leagueName?.trim() : null,
        shortName: formData?.shortName?.trim() || null
      };

      await onSubmit(submitData);
    } catch (error) {
      console.error('Form submission error:', error);
      setErrors({ submit: 'Failed to save team. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[1200]">
      <div className="bg-card rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-foreground">
              {team ? 'Edit Team' : 'Add New Team'}
            </h2>
            <button
              onClick={onCancel}
              className="p-2 hover:bg-muted rounded-lg transition-colors">
              <Icon name="X" className="w-5 h-5 text-muted-foreground" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Team Type */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Team Type <span className="text-destructive">*</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, teamType: 'country' })}
                  className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-colors text-sm font-medium ${
                    formData?.teamType === 'country' ?'border-primary bg-primary/10 text-primary' :'border-border bg-background text-muted-foreground hover:border-primary/50'
                  }`}>
                  <Icon name="Flag" className="w-4 h-4" />
                  Country Team
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, teamType: 'league' })}
                  className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-colors text-sm font-medium ${
                    formData?.teamType === 'league' ?'border-primary bg-primary/10 text-primary' :'border-border bg-background text-muted-foreground hover:border-primary/50'
                  }`}>
                  <Icon name="Trophy" className="w-4 h-4" />
                  League Team
                </button>
              </div>
              {formData?.teamType === 'league' && (
                <p className="text-xs text-muted-foreground mt-1">
                  League teams can include players from any country
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Team Name <span className="text-destructive">*</span>
              </label>
              <Input
                type="text"
                value={formData?.name}
                onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
                placeholder={formData?.teamType === 'league' ? 'e.g. Mumbai Indians' : 'Enter team name'}
                className={errors?.name ? 'border-destructive' : ''}
              />
              {errors?.name && (
                <p className="text-sm text-destructive mt-1">{errors?.name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Short Name / Initials (Optional)
              </label>
              <Input
                type="text"
                value={formData?.shortName}
                onChange={(e) => setFormData({ ...formData, shortName: e?.target?.value?.toUpperCase()?.slice(0, 5) })}
                placeholder={formData?.teamType === 'league' ? 'e.g. MI' : 'e.g. IND'}
                maxLength={5}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Short initials shown in compact views (max 5 characters)
              </p>
            </div>

            {formData?.teamType === 'league' ? (
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  League Name <span className="text-destructive">*</span>
                </label>
                <Input
                  type="text"
                  value={formData?.leagueName}
                  onChange={(e) => setFormData({ ...formData, leagueName: e?.target?.value })}
                  placeholder="e.g. IPL, BBL, CPL"
                  className={errors?.leagueName ? 'border-destructive' : ''}
                />
                {errors?.leagueName && (
                  <p className="text-sm text-destructive mt-1">{errors?.leagueName}</p>
                )}
              </div>
            ) : null}

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                {formData?.teamType === 'league' ? 'Home Country (Optional)' : 'Country'}{' '}
                {formData?.teamType === 'country' && <span className="text-destructive">*</span>}
              </label>
              <Input
                type="text"
                value={formData?.country}
                onChange={(e) => setFormData({ ...formData, country: e?.target?.value })}
                placeholder={formData?.teamType === 'league' ? 'e.g. India (optional)' : 'Enter country name'}
                className={errors?.country ? 'border-destructive' : ''}
              />
              {errors?.country && (
                <p className="text-sm text-destructive mt-1">{errors?.country}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Logo URL (Optional)
              </label>
              <Input
                type="text"
                value={formData?.logo}
                onChange={(e) => setFormData({ ...formData, logo: e?.target?.value })}
                placeholder="https://example.com/logo.png"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Leave blank to use default logo
              </p>
            </div>

            {errors?.submit && (
              <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                <p className="text-sm text-destructive">{errors?.submit}</p>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                className="flex-1"
                disabled={submitting}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="default"
                className="flex-1"
                disabled={submitting}>
                {submitting ? 'Saving...' : team ? 'Update Team' : 'Add Team'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TeamForm;